/* eslint-disable @typescript-eslint/no-explicit-any */
import { LocalDateTime } from 'js-joda';
import { first, max, min } from 'es-toolkit/compat';
import { oc } from 'ts-optchain';
import clsx from 'clsx';

import { IWithContextProps, consumer, core, withContext } from '@tock/coretypes';
import { Link } from 'omakase';
import { LockOptions } from 'types';
import { adjustDateForCutOff } from 'utils/constants';
import { getIsSoldOut } from 'utils/experience';
import { isLockedForSize } from 'utils/lock';
import BaseComponent from '@tock/shared/components/BaseComponent';
import PurchaseSizeSelector from 'components/business/search/PurchaseSizeSelector';

const SELECTED_TIME_IS_NOT_AVAILABLE = 3000;

interface OwnProps {
  actions?: any;
  currentLock?: core.TicketGroup;
  event: consumer.IOffering;
  onAttemptLock(localDateTime: LocalDateTime, priceCents: number, options: LockOptions): any;
  onSearch(size: number, date: string, time: string): void; // Updates the browser url query params
  waitlistUrl: string;
  selectedSize: number;
  showWaitlist: boolean;
  ticketTypeId: number;
}

interface State {
  errorMessage: string;
  showSoldOutError: boolean;
  size: number;
}

type Props = OwnProps & IWithContextProps;

class EventModalActions extends BaseComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    const size = this.getDefaultSize(props.currentLock, props.selectedSize);
    this.state = {
      errorMessage: '',
      showSoldOutError: false,
      size,
    };
  }

  getDefaultSize(currentLock: core.TicketGroup | undefined, selectedSize: number): number {
    if (currentLock) {
      const ticketSubset: core.ITicketSubset = first(currentLock.ticketSubset) || {};
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return ticketSubset.ticketCount!;
    }
    return selectedSize;
  }

  componentDidMount() {
    const { currentLock, selectedSize } = this.props;
    // This component is only shown if onOneDate is true. Due to the server logic, this necessarily, means eventDetails
    // is set. TODO is to move this info directly onto the event instead of putting it inside "event details".
    const { date, startTime } = this.props.event.eventDetails as consumer.EventDetails;
    this.props.onSearch(this.getDefaultSize(currentLock, selectedSize), date, startTime);
  }

  handleIncrementSize() {
    this.handleSelectSize(this.state.size + 1);
  }

  handleDecrementSize() {
    this.handleSelectSize(this.state.size - 1);
  }

  handleSelectSize(size: number) {
    this.setState({
      showSoldOutError: false,
      size,
    });
    // This component is only shown if onOneDate is true. Due to the server logic, this necessarily, means eventDetails
    // is set. TODO is to move this info directly onto the event instead of putting it inside "event details".
    const { date, startTime } = this.props.event.eventDetails as consumer.EventDetails;
    this.props.onSearch(size, date, startTime);
  }

  handleContinue() {
    if (this.state.showSoldOutError) {
      return;
    }
    // This component is only shown if onOneDate is true. Due to the server logic, this necessarily, means eventDetails
    // is set. TODO is to move this info directly onto the event instead of putting it inside "event details".
    const { date, priceCents, startTime } = this.props.event.eventDetails as consumer.EventDetails;
    const dateTimeToLock = adjustDateForCutOff(date, startTime);
    const options: LockOptions = {
      showError: true,
    };
    this.props
      .onAttemptLock(dateTimeToLock, priceCents, options)
      .then(() => {
        this.setState({
          errorMessage: '',
          showSoldOutError: false,
        });
      })
      .catch((error: any) => {
        this.handleOnError(error);
      });
  }

  handleOnError(error: any) {
    const errorCode = (oc(error) as any).error.errorCode(false) ? error.error.errorCode.toInt() : 0;
    if (errorCode === SELECTED_TIME_IS_NOT_AVAILABLE) {
      this.setState({
        showSoldOutError: true,
      });
    } else {
      this.props.actions.toast.showError(error.error.message);
    }
  }

  handleSizeChanged() {
    this.setState({
      showSoldOutError: false,
    });
  }

  getContinueButtonText(isSizeLocked: boolean, offering: consumer.IOffering): string {
    if (this.state.showSoldOutError) {
      return 'Limited availability, select another size';
    }
    const continueButtonText = isSizeLocked ? 'Holding for you' : 'Continue to checkout';
    const isSoldOut = getIsSoldOut(offering) && !isSizeLocked;
    return isSoldOut ? 'Sold out' : continueButtonText;
  }

  render() {
    const { currentLock, event, showWaitlist, ticketTypeId, waitlistUrl } = this.props;

    const { size, showSoldOutError } = this.state;

    const isSizeLocked = isLockedForSize(ticketTypeId, size, currentLock);

    const continueButtonText = this.getContinueButtonText(isSizeLocked, event);
    const isSoldOut = getIsSoldOut(event) && !isSizeLocked;

    const continueButtonClassName = clsx('EventModalActions--continueButton', {
      'is-locked': isSizeLocked,
      'is-sold-out': isSoldOut,
      'is-size-sold-out': showSoldOutError,
    });

    return (
      <div className="SearchModal-body">
        <div className="EventModalActions">
          <div className="EventModalActions--row">
            <div className="EventModalActions--guestSelector">
              <PurchaseSizeSelector
                minSize={min(event.purchaseSize) || 0}
                maxSize={max(event.purchaseSize) || 0}
                onDecrement={this.handleDecrementSize}
                onIncrement={this.handleIncrementSize}
                selectedSize={size}
                partySizeUnit="ticket"
              />
            </div>
          </div>
          <div className="EventModalActions--row2">
            <button className={continueButtonClassName} onClick={this.handleContinue}>
              {continueButtonText}
            </button>
          </div>
        </div>
        {showWaitlist && isSoldOut && (
          <Link
            href={waitlistUrl}
            size="medium"
            className="EventModalActions---reservationWaitlistLink"
          >
            Notify
          </Link>
        )}
      </div>
    );
  }
}

export default withContext(EventModalActions);
